/**
 * Sprint T-18 · Notification Studio · Analytics orchestrator tab.
 *
 * Range picker · counters · throughput · errors × 2 · latency.
 * All data comes from /api/notifications/studio/analytics/*.
 */
import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  useAnalyticsSummary,
  useAnalyticsThroughput,
  useAnalyticsErrors,
  useAnalyticsLatency,
} from "../../hooks/useNotifications";
import AnalyticsCounters from "./shared/AnalyticsCounters";
import AnalyticsThroughputChart from "./shared/AnalyticsThroughputChart";
import { ErrorsTable, LatencyTable } from "./shared/AnalyticsTables";

const RANGES = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
];

export default function AnalyticsTab() {
  const [range, setRange] = useState("24h");
  const bucket = range === "24h" || range === "7d" ? "1h" : "1d";

  const summaryQ = useAnalyticsSummary({ range });
  const thrQ = useAnalyticsThroughput({ range, bucket });
  const errProvQ = useAnalyticsErrors({ groupBy: "provider", range });
  const errTplQ = useAnalyticsErrors({ groupBy: "template", range });
  const latQ = useAnalyticsLatency({ range });

  const refetchAll = () => {
    summaryQ.refetch();
    thrQ.refetch();
    errProvQ.refetch();
    errTplQ.refetch();
    latQ.refetch();
  };

  return (
    <div className="space-y-4" data-testid="studio-analytics-tab">
      {/* Range picker */}
      <div
        className="flex flex-wrap items-center gap-2 rounded border border-[var(--ag-border)] bg-[var(--ag-panel)] p-2"
        data-testid="studio-analytics-range-picker"
      >
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRange(r.key)}
            data-testid={`studio-analytics-range-${r.key}-btn`}
            className={`rounded border px-2.5 py-1 text-[11px] transition-colors ${
              range === r.key
                ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/10 text-[var(--ag-text)]"
                : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
            }`}
          >
            {r.label}
          </button>
        ))}
        <button
          type="button"
          onClick={refetchAll}
          data-testid="studio-analytics-refresh-btn"
          className="ml-auto flex items-center gap-1 rounded border border-[var(--ag-border)] px-2.5 py-1 text-[11px] text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
        >
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      <AnalyticsCounters data={summaryQ.data} loading={summaryQ.isLoading} />
      <AnalyticsThroughputChart data={thrQ.data} loading={thrQ.isLoading} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ErrorsTable
          data={errProvQ.data}
          loading={errProvQ.isLoading}
          title="Errors by provider"
          testid="studio-analytics-errors-provider"
        />
        <ErrorsTable
          data={errTplQ.data}
          loading={errTplQ.isLoading}
          title="Errors by template"
          testid="studio-analytics-errors-template"
        />
      </div>

      <LatencyTable data={latQ.data} loading={latQ.isLoading} />
    </div>
  );
}
